// src/components/Outlook.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
// La pastiglia del tipo evento NON si ridichiara qui: forma `.qpsc-pill`
// (AdminTools.css) e colori `.ccl-et-type--*` (EventsTable.css), gli stessi
// della tabella di /calendar. Import espliciti, come EventsTable.jsx fa con
// PaymentsModal.css (cantiere OUTLOOK, Fetta 2, 27/9/2026).
import "./AdminTools.css";
import "./EventsTable.css";
import "./Outlook.css";
import HomeShell from "./HomeShell";
// `getWeekNumber` e `localKey` (chiave data locale, no ISO!): sede unica.
import { getWeekNumber, localKey } from "../utils/calendario";
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from "../api/api";

// ---------- Helpers ----------
function isMobile() {
  return window.innerWidth <= 650;
}

// Parse locale-safe (evita shift giorno)
function parseEventDate(ev) {
  if (!ev || !ev.date) return null;
  const t = (ev.time && /^\d{2}:\d{2}$/.test(ev.time)) ? ev.time : "00:00";
  const [hh, mm] = t.split(":").map(Number);

  if (/^\d{4}-\d{2}-\d{2}$/.test(ev.date)) {
    const [y, m, d] = ev.date.split("-").map(Number);
    return new Date(y, m - 1, d, hh, mm);
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(ev.date)) {
    const [d, m, y] = ev.date.split("-").map(Number);
    return new Date(y, m - 1, d, hh, mm);
  }
  return null;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth() === b.getMonth() &&
         a.getDate() === b.getDate();
}
function startOfWeekMonday(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0=Mon ... 6=Sun
  x.setDate(x.getDate() - day);
  x.setHours(0,0,0,0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfMonth(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), 1);
  x.setHours(0,0,0,0);
  return x;
}

// ---------- Component ----------
export default function Outlook() {
  const navigate = useNavigate();
  const location = useLocation();

  // LA VIA D'USCITA — ripiego di MemberProfileModal.jsx (cantiere D7CAL,
  // 1/10/2026, registro D-07 residuo 4): `location.key === "default"` è il
  // modo di react-router v6 per dire «questa è la prima voce della
  // cronologia» (link incollato, segnalibro, ricaricamento). Lì `navigate(-1)`
  // uscirebbe dall'app o non farebbe nulla, e la persona resterebbe in un
  // vicolo cieco: si ripiega sull'instradatore `/home`, che manda ciascuno
  // alla home del proprio ruolo. `replace` per non lasciare dietro una voce
  // che riporterebbe qui.
  const tornaIndietro = useCallback(() => {
    if (location.key === "default") {
      navigate("/home", { replace: true });
    } else {
      navigate(-1);
    }
  }, [location.key, navigate]);

  // ordine corretto degli state (usati più sotto)
  const [cursorDate, setCursorDate] = useState(() => {
    const n = new Date(); n.setHours(0,0,0,0); return n;
  });
  const [view, setView] = useState(isMobile() ? "agenda" : "month"); // 'agenda'|'day'|'week'|'month'
  const [allEvents, setAllEvents] = useState([]);

  // carica eventi
  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/events");

        const cleaned = (Array.isArray(data) ? data : [])
          .map(e => ({
            ...e,
            team: (e.team || "").trim()
          }))
          .filter(e => e.date && parseEventDate(e));

        setAllEvents(cleaned);
      } catch (err) {
        console.error("Outlook load error:", err);
        setAllEvents([]);
      }
    })();
  }, []);

  // lista eventi ordinata (niente filtri team)
  const filtered = useMemo(() => {
    const list = allEvents.slice();
    list.sort((a, b) => +parseEventDate(a) - +parseEventDate(b));
    return list;
  }, [allEvents]);

  // range correnti per viste week/month
  const weekStart = useMemo(() => startOfWeekMonday(cursorDate), [cursorDate]);
  const weekDays  = useMemo(() => Array.from({length:7}, (_,i) => addDays(weekStart, i)), [weekStart]);

  const monthStart      = useMemo(() => startOfMonth(cursorDate), [cursorDate]);
  const monthGridStart  = useMemo(() => startOfWeekMonday(monthStart), [monthStart]);
  const monthDays       = useMemo(() => Array.from({length:42}, (_,i) => addDays(monthGridStart, i)), [monthGridStart]);

  // mappa eventi per giorno (key locale)
  const eventsByDay = useMemo(() => {
    const map = new Map();
    filtered.forEach(ev => {
      const d = parseEventDate(ev);
      if (!d) return;
      const key = localKey(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    });
    for (const arr of map.values()) {
      arr.sort((a,b) => +parseEventDate(a) - +parseEventDate(b));
    }
    return map;
  }, [filtered]);

  // agenda: +/- 60 giorni
  const agendaEvents = useMemo(() => {
    const from = addDays(cursorDate, -3);
    const to   = addDays(cursorDate, 60);
    return filtered.filter(ev => {
      const d = parseEventDate(ev);
      return d && d >= from && d <= to;
    });
  }, [filtered, cursorDate]);

  // day view
  const dayKey = useMemo(() => localKey(cursorDate), [cursorDate]);
  const dayEvents = useMemo(() => {
    return filtered.filter(ev => {
      const d = parseEventDate(ev);
      return d && localKey(d) === dayKey;
    });
  }, [filtered, dayKey]);

  // title dinamico
  const weekNum = useMemo(() => getWeekNumber(cursorDate), [cursorDate]);
  const titleText = useMemo(() => {
    const monthYear = cursorDate.toLocaleDateString("en-AU",{ month:"long", year:"numeric" });
    if (view === "day") {
      const wd = cursorDate.toLocaleDateString("en-AU",{ weekday:"long" });
      const day = cursorDate.toLocaleDateString("en-AU",{ day:"2-digit" });
      return `${monthYear} • ${day} • ${wd}`;
    }
    if (view === "week") return `${monthYear} • Week ${weekNum}`;
    return monthYear; // agenda/month
  }, [cursorDate, view, weekNum]);

  // navigazione coerente con la view
  const goToday = () => { const n = new Date(); n.setHours(0,0,0,0); setCursorDate(n); setView("day"); };
  const goPrev  = () => setCursorDate(addDays(cursorDate, view === "month" ? -30 : view === "week" ? -7 : -1));
  const goNext  = () => setCursorDate(addDays(cursorDate, view === "month" ? 30 : view === "week" ? 7 : 1));

  // ---------- UI ----------
  const fmtDayShort   = d => d.toLocaleDateString("en-AU",{ weekday:"short" }).toUpperCase();
  // Tre rami come `typeModifier` di EventsTable.jsx: Game / Training / tutto
  // il resto = Event («Bye» compreso: in /calendar non ha pastiglia propria).
  // Il confronto resta senza maiuscole, come faceva Outlook.
  const typeClass = (ev) => {
    const t = (ev.type || "").toLowerCase();
    if (t === "game") return "qpsc-pill ccl-et-type--game";
    if (t === "training") return "qpsc-pill ccl-et-type--training";
    return "qpsc-pill ccl-et-type--event";
  };

  return (
    // Telaio come /calendar (cantiere OUTLOOK, 27/9/2026): barra utente e
    // banda al posto della barra rossa; il Back torna alla pagina da cui
    // si è venuti (/calendar o /my-calendar), come MyCalendar; aperto da un
    // link, alla home (`tornaIndietro`, D7CAL).
    <HomeShell backTo={tornaIndietro} width="admin">
      {/* titolo + controlli centrati a colonna + frecce ai lati */}
      <div className="outlook-header">
        <h2 className="outlook-title">Outlook Calendar</h2>

        <div className="outlook-titlebar">
          <button className="pill nav-arrow" onClick={goPrev}>◀</button>

          <div className="controls-col">
            <button className={`pill ${view==="agenda"?"active":""}`} onClick={()=>setView("agenda")}>Agenda</button>
            <button className={`pill ${view==="day"?"active":""}`}    onClick={()=>setView("day")}>Day</button>
            <button className={`pill ${view==="week"?"active":""}`}   onClick={()=>setView("week")}>Week</button>
            <button className={`pill ${view==="month"?"active":""}`}  onClick={()=>setView("month")}>Month</button>
            <button className="pill" onClick={goToday}>Today</button>
          </div>

          <button className="pill nav-arrow" onClick={goNext}>▶</button>
        </div>

        <div className="outlook-subheader">
          <span className="period">{titleText}</span>
        </div>
      </div>

      {/* DAY */}
      {view === "day" && (
        <div className="day-wrapper">
          {dayEvents.length === 0 ? (
            <div className="agenda-empty">No events today.</div>
          ) : (
            <div className="day-col">
              {dayEvents.map(ev => (
                <div className="day-card" key={ev.id}>
                  <div className="ol-line">
                    {ev.type && <span className={typeClass(ev)}>{ev.type}</span>}
                    <span className="ol-time">{ev.time || "—"}</span>
                    {ev.type === "Game" && <span className="ol-opp">vs {ev.opponent}</span>}
                  </div>
                  <div className="ol-line">
                    <span className="ol-team">{ev.team}</span>
                    <span className="ol-loc">{ev.location}</span>
                    {ev.homeAway && <span className="ol-ha">({ev.homeAway})</span>}
                  </div>
                  {ev.notes && <div className="ol-notes">{ev.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* WEEK: header giorni + card su due righe */}
      {view === "week" && (
        <div className="week-wrapper">
          <div className="week-scroller">
            <div className="week-strip">
              {weekDays.map((d) => {
                const key = localKey(d);
                const evs = (eventsByDay.get(key) || []);
                return (
                  <div key={key} className="week-daycol">
                    <div className="week-dayhead">
                      <div className="wday">{fmtDayShort(d)}</div>
                      <div className={`wdate ${sameDay(d, new Date()) ? "today":""}`}>{d.getDate()}</div>
                    </div>
                    <div className="week-daybody">
                      {evs.length === 0 && <div className="ol-empty">—</div>}
                      {evs.map(ev => (
                        <div className="ev-item" key={ev.id}>
                          <div className="ol-line">
                            <span className="ol-time">{ev.time || "—"}</span>
                            {ev.type && <span className={typeClass(ev)}>{ev.type}</span>}
                            {ev.type === "Game" && <span className="ol-opp">{ev.opponent}</span>}
                          </div>
                          <div className="ev-sub">{ev.location} · {ev.team}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* MONTH: header allineato + card su due righe */}
      {view === "month" && (
        <div className="month-wrapper">
          <div className="month-scroller">
            <div className="month-grid">
              {monthDays.map((d, idx) => {
                const key = localKey(d);
                const evs = eventsByDay.get(key) || [];
                const inMonth = d.getMonth() === monthStart.getMonth();
                const maxShow = 3;
                const extra = Math.max(0, evs.length - maxShow);
                return (
                  <div key={idx} className={`day-cell ${inMonth?"":"dim"}`}>
                    <div className="day-head">
                      <span className="day-wk">{fmtDayShort(d)}</span>
                      <span className={`day-num ${sameDay(d, new Date()) ? "today":""}`}>{d.getDate()}</span>
                    </div>
                    <div className="day-body">
                      {evs.slice(0, maxShow).map(ev => (
                        <div className="ev-item" key={ev.id}>
                          <div className="ol-line">
                            <span className="ol-time">{ev.time || "—"}</span>
                            {ev.type && <span className={typeClass(ev)}>{ev.type}</span>}
                            {ev.type === "Game" && <span className="ol-opp">{ev.opponent}</span>}
                          </div>
                          <div className="ev-sub">{ev.location} · {ev.team}</div>
                        </div>
                      ))}
                      {extra > 0 && (
                        <div
                          className="more-chip clickable"
                          onClick={() => {
                            setCursorDate(d);
                            setView("day");
                          }}
                        >
                          +{extra} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* AGENDA */}
      {view === "agenda" && (
        <div className="agenda-wrapper">
          {agendaEvents.length === 0 && <div className="agenda-empty">No events.</div>}
          {agendaEvents.map(ev => {
            const d = parseEventDate(ev);
            return (
              <div className="agenda-card" key={ev.id}>
                <div className="agenda-left">
                  <div className="aday">{fmtDayShort(d)}</div>
                  <div className={`adate ${sameDay(d,new Date()) ? "today":""}`}>
                    {String(d.getDate()).padStart(2,"0")}/{String(d.getMonth()+1).padStart(2,"0")}
                  </div>
                </div>
                <div className="agenda-right">
                  <div className="ol-line">
                    {ev.type && <span className={typeClass(ev)}>{ev.type}</span>}
                    <span className="ol-time">{ev.time || "—"}</span>
                    {ev.type === "Game" && <span className="ol-opp">vs {ev.opponent}</span>}
                  </div>
                  <div className="ol-line">
                    <span className="ol-team">{ev.team}</span>
                    <span className="ol-loc">{ev.location}</span>
                    {ev.homeAway && <span className="ol-ha">({ev.homeAway})</span>}
                  </div>
                  {ev.notes && <div className="ol-notes">{ev.notes}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </HomeShell>
  );
}